/**
 * API configuration constants
 */

// Base URLs
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
export const API_VERSION = 'v1';

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    VALIDATE_RESET_TOKEN: '/auth/validate-reset-token',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    VERIFY_OTP: '/auth/verify-otp',
    SETUP_2FA: '/auth/setup-2fa',
    PROFILE: '/auth/profile',
    UPDATE_PROFILE: '/auth/profile'
  },

  // User Management
  USERS: {
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    CHANGE_PASSWORD: '/users/change-password',
    UPLOAD_AVATAR: '/users/avatar',
    PREFERENCES: '/users/preferences'
  },

  // SAX (SubAdmin Permissions)
  SAX: {
    MODULES_PERMISSIONS: '/admin/sax/modules-permissions',
    USER_PERMISSIONS: '/admin/sax/user-permissions',
    UPDATE_PERMISSIONS: '/admin/sax/update-permissions'
  },

  // Flight Management
  FLIGHTS: {
    SEARCH: '/flights/search',
    DETAILS: '/flights/details',
    MULTIPLE_FLIGHTS: '/flights/multiple-flights',
    AVAILABILITY: '/flights/availability',
    PRICES: '/flights/prices'
  },

  // Booking Management
  BOOKINGS: {
    CREATE: '/bookings',
    LIST: '/bookings',
    DETAILS: '/bookings',
    HISTORY: '/bookings/history',
    CANCEL: '/bookings/cancel',
    MODIFY: '/bookings/modify',
    CHECK_IN: '/bookings/check-in',
    COMPLETED: '/bookings/completed',
    CANCELLED: '/bookings/cancelled',
    INVOICE: '/bookings/invoice',
    REMOVE_PASSENGER: '/bookings/passenger',
    BY_REFERENCE: '/bookings/reference',
    ADMIN_LIFECYCLE: '/admin/bookings/lifecycle',
    LOCK_SEATS: '/bookings/lock-seats',
    REVOKE_LOCK: '/bookings/revoke-lock'
  },

  // Aircraft Management
  AIRCRAFT: {
    LIST: '/aircraft',
    DETAILS: '/aircraft',
    CREATE: '/aircraft',
    UPDATE: '/aircraft',
    DELETE: '/aircraft',
    SEAT_MAP: '/aircraft/seat-map'
  },

  // Airport Management
  AIRPORTS: {
    LIST: '/airports',
    SEARCH: '/airports/search',
    DETAILS: '/airports'
  },

  // Payment
  PAYMENTS: {
    PROCESS: '/payments/process',
    METHODS: '/payments/methods',
    HISTORY: '/payments/history',
    CONFIRM_FREE_REBOOKING: '/payments/rebooking/confirm-free'
  },

  // Push (FCM)
  PUSH: {
    CONFIG: '/push/config',
    REGISTER: '/push/register',
    UNREGISTER: '/push/unregister',
    TEST: '/push/test'
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    DETAILS: '/notifications',
    CREATE: '/notifications',
    UPDATE: '/notifications',
    DELETE: '/notifications',
    MARK_READ: '/notifications/mark-read',
    MARK_ALL_READ: '/notifications/mark-all-read',
    UNREAD_COUNT: '/notifications/unread-count',
    BULK_DELETE: '/notifications/bulk-delete',
    PREFERENCES: '/notifications/preferences'
  },

  // Passengers
  PASSENGERS: {
    LIST: '/passengers',
    DETAILS: '/passengers',
    CREATE: '/passengers',
    UPDATE: '/passengers',
    DELETE: '/passengers'
  },

  // Two-Factor Authentication
  TWO_FACTOR: {
    ENABLE: '/two-factor/enable',
    DISABLE: '/two-factor/disable',
    SEND_EMAIL_OTP: '/two-factor/send-email-otp',
    SEND_SMS_OTP: '/two-factor/send-sms-otp',
    VERIFY_OTP: '/two-factor/verify-otp',
    SETUP_SECURITY_QUESTIONS: '/two-factor/setup-security-questions',
    VERIFY_SECURITY_QUESTIONS: '/two-factor/verify-security-questions',
    GET_SECURITY_QUESTIONS: '/two-factor/security-questions'
  },

  // Utilities
  UTILITY: {
    COUNTRIES: '/utility/countries',
    NATIONALITIES: '/utility/nationalities',
    SECURITY_QUESTIONS: '/utility/security-questions',
    TIMEZONES: '/utility/timezones',
    WORLD_TIMEZONES: '/utility/world-timezones',
    COUNTRY_BY_PHONE: '/utility/country/phone-code',
    HEALTH: '/utility/health'
  },

  // Foreign Exchange
  FX: {
    RATES: '/fx/rates',
    CONVERT: '/fx/convert',
    CURRENCIES: '/fx/currencies',
    PREFERENCE: '/fx/preference',
    CACHE_STATUS: '/fx/cache/status'
  },

  // Admin FX Management
  ADMIN_FX: {
    SETTINGS: '/admin/fx/settings',
    REFRESH: '/admin/fx/refresh',
    AUDIT_LOGS: '/admin/fx/audit-logs',
    REFRESH_RATES: '/admin/fx/refresh-rates'
  },

  // Contract Management
  CONTRACTS: {
    LIST: '/admin/contracts',
    CREATE: '/admin/contracts',
    DETAIL: '/admin/contracts',
    UPDATE: '/admin/contracts',
    DELETE: '/admin/contracts',
    RESTORE: '/admin/contracts',
    DOWNLOAD: '/admin/contracts',
    REMIND: '/admin/contracts',
    VOID: '/admin/contracts',
    SIGNATURE_STATUS: '/admin/contracts',
    EXPORT_EXCEL: '/admin/contracts/export/excel',
    TEMPLATES: '/admin/contracts/templates',
    TEMPLATE_UPLOAD: '/admin/contracts/templates/upload'
  }
};

// HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500
};

// Request Timeouts (in milliseconds)
export const TIMEOUTS = {
  DEFAULT: 10000,
  UPLOAD: 30000,
  LONG_RUNNING: 60000
};

// Request Headers
export const HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
  ACCEPT: 'Accept'
};

// Content Types
export const CONTENT_TYPES = {
  JSON: 'application/json',
  FORM_DATA: 'multipart/form-data',
  URL_ENCODED: 'application/x-www-form-urlencoded'
};